import React, { useEffect, useState } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import ModuleFrame, { Chip, KV, useModuleTab } from '../../components/ModuleFrame';
import { AppFocusChips, useSelectedApp, withApp } from '../../components/AppSelector';
import { designLabel } from '../../components/InstanceLink';
import { useOperate, AppStatusStrip, OperateTab } from '../../components/AppOperate';
import Lab from '../../components/Lab';
import BuildHandoff from '../../components/builder/BuildHandoff';
import LikenessTake from '../../components/builder/LikenessTake';
import { createAppWithSeed, updateEntity, useEntityList, friendlyError, roleSatisfies } from '../../data/entities';
import { useCan } from '../../data/permissions';
import { useAuth } from '../../AuthContext';
import { askNova } from '../../components/ConsoleHeader';
import { STARTER_PROMPTS, TAB_ALIAS } from '../../data/consoleCatalog';
import LifecycleRail from '../../components/LifecycleRail';
import ConceptScreens from '../../components/ConceptScreens';
import { signalsFrom, stageBrief, nextStep } from '../../data/lifecycle';

const BUILD_PROMPT = STARTER_PROMPTS.find((p) => p.attach) || STARTER_PROMPTS[0];

// App Builder — the studio where an app is composed and taken live. Three
// stages on the lifecycle rail (decision lab-is-the-studio, 2026-10-14):
// LAB (think it through, design it, prove it — one screen per phase, the
// Design/Surfaces/Try phases host what used to be this page's Design,
// Surfaces and Preview tabs), READY TO SHIP (gates, the first build on the
// instance, release) and OPERATE. One derived `nextStep` drives the rail's
// checks and disabled states, the stage brief, the Next button and NoVA.
// Old ?tab= links (design|knowledge|data|policies|surfaces|preview) are
// rewritten to the matching Lab phase on arrival (catalog TAB_ALIAS).

export default function AppBuilderModule() {
  const { org, myRole } = useOutletContext();
  const { user } = useAuth();
  const { apps, archived, app, loading, select } = useSelectedApp();
  const { docs: knowledgeDocs } = useEntityList('knowledgeDoc', { org: org.id });
  const { docs: integrations } = useEntityList('integration', { org: org.id });
  const { docs: agents } = useEntityList('agent', { org: org.id });
  const { docs: suites } = useEntityList('testSuite', { org: org.id });
  const { docs: runs } = useEntityList('testRun', { org: org.id });
  const { docs: releases } = useEntityList('appRelease', { org: org.id });
  const { docs: instances } = useEntityList('appInstance', { org: org.id }, { includeOrgWide: true });
  const [creating, setCreating] = useState(false);
  const [rawTab, setTab] = useModuleTab('lab');
  const alias = TAB_ALIAS[rawTab] || null;
  const tab = alias ? 'lab' : rawTab;
  useEffect(() => {
    // Old six-tab URLs land on the Lab phase that absorbed them.
    if (alias) setTab('lab', { phase: alias.phase, card: alias.card || null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawTab]);
  const isAdmin = roleSatisfies(myRole, 'admin');
  const can = useCan(org, myRole);
  const canEdit = can('app.update');
  const canAgents = can('agent.create'); // create / shape / install agents
  // The app's life after design: instance health, releases, conversations,
  // review queue, suggestions, the first build. Polled; drives the strip,
  // Operate and the Build handoff.
  const ops = useOperate(org.id, app?.id);

  const kDocs = (knowledgeDocs || []).filter((d) => (d.appIds || []).includes(app?.id));
  const kLive = kDocs.filter((d) => d.status === 'live');
  const integs = (integrations || []).filter((d) => (d.appIds || []).includes(app?.id));
  const agentIds = app?.links?.agentIds || [];
  const attached = agentIds.map((id) => (agents || []).find((a) => a.id === id)).filter(Boolean);
  const unattached = (agents || []).filter((a) => !agentIds.includes(a.id));
  const suite = (suites || []).find((s) => s.appId === app?.id) || null;
  const lastRun = (runs || []).filter((r) => r.appId === app?.id).sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0))[0] || null;
  const release = (releases || []).filter((r) => r.appId === app?.id).sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0))[0] || null;
  const instance = (instances || []).find((i) => i.id === app?.links?.instanceAppId) || null;
  const pipelineTo = app?.links?.pipelineId && app?.links?.workspaceId
    ? `/orgs/${org.id}/ws/${app.links.workspaceId}/pipelines/${app.links.pipelineId}`
    : withApp(`/orgs/${org.id}/workflows`, app);

  // Seeded-vs-done (2026-10-04 audit F10): defaults never read as earned ✓s.
  const seeded = app?.links?.seeded || {};
  const seededDoc = kDocs.some((d) => (d.tags || []).includes('seeded'));
  const seededSuite = !!suite && (seeded.suite || /— baseline$/.test(suite.name || ''));
  const onlySeededKnowledge = kLive.length > 0 && kLive.every((d) => (d.tags || []).includes('seeded'));
  const checklist = app ? [
    { label: 'An agent is attached', ok: attached.length > 0, tab: 'lab', extra: { phase: 'design' } },
    { label: 'Knowledge approved & live', ok: kLive.length > 0, to: withApp(`/orgs/${org.id}/knowledge`, app),
      note: onlySeededKnowledge ? 'only the starter policy is live — add your own documents' : kLive.length === 0 && seededDoc ? 'starter policy is a draft — approve and publish it, add your documents' : '' },
    { label: 'Guardrails configured', ok: !!org.guardrails && Object.values(org.guardrails).some(Boolean), to: withApp(`/orgs/${org.id}/knowledge?tab=guardrails`, app),
      note: 'organization-wide defaults — review once for this app' },
    { label: 'Workflow built', ok: !!app.links?.pipelineId, to: pipelineTo,
      note: app.links?.pipelineId && seeded.pipeline ? 'drafted by NoVA — open the canvas and review it' : '' },
    { label: 'Integrations tested', ok: integs.length > 0 && integs.every((i) => i.lastTest?.ok), to: withApp(`/orgs/${org.id}/integrations`, app) },
    { label: 'QA suite passed', ok: lastRun?.status === 'passed', to: withApp(`/orgs/${org.id}/qa`, app),
      note: seededSuite && !lastRun ? 'baseline suite was added for you — run it, then add cases for this app' : '' },
    { label: 'Instance linked', ok: !!instance, tab: 'lab', extra: { phase: 'surfaces' } },
    { label: 'Release approved', ok: ['approved', 'released'].includes(release?.status), to: withApp(`/orgs/${org.id}/deployments`, app) },
    // Lab (add-on): informational — never gates readiness.
    { label: 'Concept accepted', ok: !!ops.data?.lab?.concept?.accepted, tab: 'lab', extra: { phase: 'concept' }, optional: true,
      note: ops.data?.lab?.concept?.accepted ? `"${ops.data.lab.concept.accepted.title}" v${ops.data.lab.concept.accepted.version}` : ops.data?.lab?.concept?.latest ? `v${ops.data.lab.concept.latest.version} drafted — review it with NoVA` : 'optional — talk the idea through with NoVA in the Lab' },
    { label: 'Experience tested in a browser', ok: ops.data?.lab?.lastTest?.verdict === 'pass', tab: 'lab', extra: { phase: 'test' }, optional: true,
      note: ops.data?.lab?.lastTest ? `last run ${ops.data.lab.lastTest.verdict} on ${ops.data.lab.lastTest.env || 'the app'}${ops.data.lab.stories?.total ? ` · ${ops.data.lab.stories.verified}/${ops.data.lab.stories.total - (ops.data.lab.stories.proposed || 0)} stories verified` : ''}` : 'optional — Lab add-on' },
  ] : [];
  const ready = checklist.length > 0 && checklist.filter((c) => !c.optional).every((c) => c.ok);

  const setLinks = (patch) => updateEntity('app', { org: org.id }, app.id, { links: { ...(app.links || {}), ...patch } });
  const attach = (id) => setLinks({ agentIds: [...new Set([...agentIds, id])] });
  const detach = (id) => setLinks({ agentIds: agentIds.filter((x) => x !== id) });

  const requiredRows = checklist.filter((c) => !c.optional);
  const lifeCounts = {
    ship: app ? `${requiredRows.filter((c) => c.ok).length}/${requiredRows.length}` : undefined,
    operate: ops.data?.suggestions?.filter((x) => x.severity === 'critical' || x.severity === 'high').length || undefined,
    lab: (((ops.data?.lab?.awaiting || 0) + (ops.data?.lab?.stories?.failing || 0) + (ops.data?.lab?.lastPerf?.verdict === 'fail' ? 1 : 0)) || ops.data?.lab?.active || undefined),
  };
  const build = ops.data?.build || app?.build || null;
  const lifeSignals = signalsFrom({
    app,
    org,
    agents: attached,
    liveDocs: kLive,
    instance,
    integrations: integs,
    lastRun,
    release,
    lab: ops.data?.lab,
    build,
  });
  const next = app ? nextStep({ app, signals: lifeSignals }) : null;
  const brief = app ? stageBrief({
    stage: tab,
    app,
    concept: ops.data?.lab?.concept,
    checklist,
    agent: attached[0] || null,
    kLive: kLive.length,
    instance,
    stories: ops.data?.lab?.stories,
    next,
    build,
  }) : null;

  // Keep app.stage in step with the next step (other surfaces read it).
  useEffect(() => {
    if (!app || !next) return;
    if (app.stage !== next.stage) updateEntity('app', { org: org.id }, app.id, { stage: next.stage }).catch(() => {});
  }, [app, next, org.id]);

  // Everything the Lab's Design / Surfaces / Try phases need from this page.
  const studio = app ? { user, ops, attached, unattached, kDocs, kLive, integs, instance, instances, pipelineTo, can, canEdit, canAgents, isAdmin, attach, detach, setTab } : null;

  // Archived apps leave every picker and NoVA's candidates; the chooser
  // below can show and restore them. Archiving drops the focus.
  const setArchived = async (target, archived) => {
    await updateEntity('app', { org: org.id }, target.id, { archived }).catch(() => {});
    if (archived && target.id === app?.id) select(null);
  };

  return (
    <ModuleFrame
      title="App Builder"
      subtitle="Lab → Ready to ship → Operate"
      accent="#14e0c8"
      note={app
        ? `App Builder, app "${app.name}" (id ${app.id}), stage ${next?.stage || app.stage || 'lab'}, tab ${tab}. Next step: ${next?.label || 'none'}. Ship checklist ${lifeCounts.ship || '—'}. NoVA: build_app, start_studio, instance_status, sync_instance, navigate_app.`
        : `App Builder with ${apps.length} app${apps.length === 1 ? '' : 's'}, none focused${creating ? ', new-app form open' : ''}. Pick an app in the chips or ask NoVA to build_app.`}
      help="Think the app through, design it and prove it in the Lab; send it to the instance's Dev Console for the build and clear the gates under Ready to ship; then watch it under Operate. The Next button always names the one thing to do."
      context={(
        <>
          <AppFocusChips />
          {apps.length > 0 && (
            <button type="button" className="cs-btn cs-btn-outline cs-btn-sm" onClick={() => setCreating((v) => !v)}>
              {creating ? 'Cancel' : '+ New app'}
            </button>
          )}
          {app && <Chip tone={lifeSignals.operate ? 'ok' : 'info'}>{lifeSignals.operate ? 'live' : attached.length ? 'in the Lab' : 'needs an agent'}</Chip>}
          {app && canEdit && !creating && (
            <button type="button" className="cs-btn cs-btn-outline cs-btn-sm" title="Hide this app from every picker; restore it from the app chooser" onClick={() => { if (window.confirm(`Archive "${app.name}"? It leaves the pickers and NoVA's list; nothing is deleted and you can restore it.`)) setArchived(app, true); }}>
              Archive
            </button>
          )}
        </>
      )}
      lead={app && !creating ? (
        <LifecycleRail variant="tabs" here={tab} onSelect={setTab} signals={lifeSignals} counts={lifeCounts} brief={brief} next={next} />
      ) : null}
      verbs={app ? [
        { label: 'Try it as a visitor', hint: 'Lab › Try', onClick: () => setTab('lab', { phase: 'try' }) },
        { label: 'Put it on a website', hint: 'Lab › Surfaces', onClick: () => setTab('lab', { phase: 'surfaces' }) },
        { label: 'Build it on the instance', hint: 'Ready to ship', onClick: () => setTab('ship') },
        { label: 'How is it doing?', hint: 'Operate', onClick: () => setTab('operate') },
        { label: 'Ask NoVA what to do next', hint: 'copilot', onClick: () => askNova(`For my app "${app.name}" (goal: ${app.goal || 'unset'}): it has ${attached.length} agent(s), ${kLive.length} live documents of ${kDocs.length}, ${instance ? 'a hosted instance' : 'no hosted instance'}. The console says the next step is "${next?.label || ''}". Walk me through it.`) },
      ] : null}
    >
      {loading && <p className="cs-help">Loading apps…</p>}
      {!loading && (!apps.length || creating) && (
        <NewAppForm orgId={org.id} uid={user.uid} first={!apps.length} onDone={(id) => { setCreating(false); select(id); }} />
      )}
      {!loading && apps.length > 0 && !app && !creating && (
        <AppChooser apps={apps} archived={archived} select={select} canEdit={canEdit} onNew={() => setCreating(true)} onRestore={(a) => setArchived(a, false)} />
      )}

      {app && !creating && (attached.length > 0 || instance) && (
        <AppStatusStrip org={org} app={app} data={ops.data} tab={tab} onTab={setTab} />
      )}

      {app && !creating && app.links?.likeness && (
        <LikenessTake org={org} app={app} uid={user.uid} isAdmin={isAdmin} />
      )}

      {app && !creating && tab === 'lab' && (
        <Lab org={org} app={app} isAdmin={isAdmin} canEdit={can('lab.run')} canApply={can('lab.apply')} canApprove={can('lab.approveWrites')} canCredentials={can('lab.credentials')} canBuild={can('instance.sync')} onTab={setTab} ops={ops} studio={studio} defaultPhase={next?.stage === 'lab' && next.phase ? next.phase : 'design'} />
      )}

      {app && !creating && tab === 'operate' && (
        <>
          <ConceptScreens org={org} appId={app.id} canRevise={can('lab.run')} onOpen={(screen, o) => setTab('lab', { phase: 'concept', fig: 'screens', screen, revise: o?.revise ? 1 : null })} />
          <OperateTab org={org} app={app} data={ops.data} err={ops.err} uid={user.uid} isAdmin={isAdmin} canEdit={can('instance.featureChecks')} onTab={setTab} reload={ops.reload} />
        </>
      )}

      {app && !creating && tab === 'ship' && (
        <>
          <BuildHandoff org={org} app={app} instance={instance} instances={instances} ops={ops} next={next} canBuild={can('instance.sync')} canLink={canEdit} onTab={setTab} />
          <ConceptScreens org={org} appId={app.id} variant="ship" onOpen={() => setTab('lab', { phase: 'stories' })} />
          <ModuleFrame.Columns cols={2}>
            <ModuleFrame.Panel title="Release checklist" meta={ready ? 'ready to release' : `${checklist.filter((c) => c.ok && !c.optional).length}/${checklist.filter((c) => !c.optional).length} complete`}>
              <ul className="mf-list">
                {checklist.map((c) => (
                  <li key={c.label} className="mf-row">
                    <Chip tone={c.ok ? (c.note ? 'info' : 'ok') : 'muted'}>{c.ok ? '✓' : '○'}</Chip>
                    <span className="mf-row-grow">
                      {c.label}
                      {c.note && <span className="mf-muted" style={{ display: 'block', fontSize: 12 }}>{c.note}</span>}
                    </span>
                    {c.tab ? <button type="button" className="mf-muted cs-btn-link" onClick={() => setTab(c.tab, c.extra)}>open →</button> : <Link to={c.to} className="mf-muted">open →</Link>}
                  </li>
                ))}
              </ul>
            </ModuleFrame.Panel>
            <ModuleFrame.Panel title="Where things stand" meta="linked records">
              <KV rows={[
                ['Agents', attached.length ? attached.map((a) => a.name).join(', ') : 'none'],
                ['Channels', (app.channels || []).join(', ') || '—'],
                ['Workflow', app.links?.pipelineId ? <Link to={pipelineTo}>open canvas</Link> : 'not linked'],
                ['Integrations', integs.length ? integs.map((i) => i.name).join(', ') : 'none'],
                ['Test suite', suite ? suite.name : 'none'],
                ['Instance', instance ? `${instance.name || instance.slug} · ${instance.status}` : 'not linked — see Build on the instance above'],
                ['Last release', release ? `${release.version} · ${release.status}` : '—'],
              ]} />
              <div className="ab-next-actions" style={{ marginTop: 10 }}>
                <Link to={withApp(`/orgs/${org.id}/qa`, app)} className="cs-btn cs-btn-outline cs-btn-sm">Run tests</Link>
                <Link to={withApp(`/orgs/${org.id}/deployments`, app)} className={`cs-btn cs-btn-sm ${ready ? 'cs-btn-primary' : 'cs-btn-outline'}`}>{ready ? 'Release' : 'DevOps'}</Link>
              </div>
            </ModuleFrame.Panel>
          </ModuleFrame.Columns>
        </>
      )}
    </ModuleFrame>
  );
}

// No app in focus: pick one explicitly (decision app-focus-context — the
// studio never silently defaults to the newest app). Archived apps sit
// behind a toggle with Restore.
const STAGE_LABEL = { lab: 'in the Lab', ship: 'ready to ship', operate: 'live', live: 'live', deploy: 'deploying', test: 'testing', build: 'building' };
function AppChooser({ apps, archived, select, canEdit, onNew, onRestore }) {
  const [showArchived, setShowArchived] = useState(false);
  return (
    <ModuleFrame.Panel
      title="Pick the app to work on"
      meta={`${apps.length} app${apps.length === 1 ? '' : 's'} in this workspace · the choice is remembered`}
      actions={<button type="button" className="cs-btn cs-btn-outline cs-btn-sm" onClick={onNew}>+ New app</button>}
    >
      <ul className="mf-list">
        {apps.map((a) => (
          <li key={a.id}>
            <button type="button" className="mf-row" style={{ width: '100%', cursor: 'pointer', color: 'var(--text)' }} onClick={() => select(a.id)}>
              <span className="mf-row-grow" style={{ textAlign: 'left' }}>
                <strong>{designLabel(a, apps)}</strong>
                {a.goal && <span className="mf-muted" style={{ display: 'block', fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.goal}</span>}
              </span>
              <Chip tone={a.stage === 'operate' || a.stage === 'live' ? 'ok' : 'muted'}>{STAGE_LABEL[a.stage] || a.stage || 'in the Lab'}</Chip>
              <span className="mf-muted">open →</span>
            </button>
          </li>
        ))}
      </ul>
      {archived.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <button type="button" className="mf-muted cs-btn-link" onClick={() => setShowArchived((v) => !v)}>
            {showArchived ? 'Hide' : 'Show'} {archived.length} archived
          </button>
          {showArchived && (
            <ul className="mf-list" style={{ marginTop: 6 }}>
              {archived.map((a) => (
                <li key={a.id} className="mf-row">
                  <span className="mf-row-grow mf-muted">{a.name}</span>
                  {canEdit && <button type="button" className="cs-btn cs-btn-outline cs-btn-sm" onClick={() => onRestore(a)}>Restore</button>}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </ModuleFrame.Panel>
  );
}

function NewAppForm({ orgId, uid, first, onDone }) {
  const [name, setName] = useState('');
  const [goal, setGoal] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setBusy(true); setErr('');
    try {
      const { appId, seedError } = await createAppWithSeed(orgId, { name: name.trim(), goal: goal.trim(), createdBy: uid });
      if (seedError) {
        // The app exists; only its policy doc / baseline suite failed to seed.
        setErr(`App created, but the enterprise seed (policy document + baseline suite) failed: ${seedError}. You can add them in Knowledge and QA.`);
        setTimeout(() => onDone(appId), 2500);
        return;
      }
      onDone(appId);
    } catch (ex) {
      setErr(friendlyError(ex));
    } finally {
      setBusy(false);
    }
  };
  return (
    <ModuleFrame.Panel title={first ? 'Define your first app' : 'Define a new app'} meta="a name and the outcome it must deliver">
      {first && (
        <div className="ab-nova-path">
          <p className="mf-muted" style={{ margin: 0 }}>
            Fastest way: tell NoVA what the app should do and attach the documents it should know. It creates the app,
            its agent and the knowledge, and shows you the plan before changing anything.
          </p>
          <button type="button" className="cs-btn cs-btn-primary cs-btn-sm" onClick={() => askNova(BUILD_PROMPT.text, { attach: true })}>
            ◈ Build it with NoVA
          </button>
          <p className="mf-muted" style={{ margin: 0 }}>Or fill in the two fields below yourself:</p>
        </div>
      )}
      <form onSubmit={submit} style={{ display: 'grid', gap: 8, maxWidth: 560 }}>
        <input className="cs-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="App name — e.g. Customer Service Assistant" disabled={busy} aria-label="App name" />
        <textarea className="mf-textarea" value={goal} onChange={(e) => setGoal(e.target.value)} placeholder="Goal — what must it do for whom? e.g. Answer customer questions from approved SOPs, route exceptions to a human." disabled={busy} aria-label="App goal" />
        {err && <p className="cs-form-error" role="alert">{err}</p>}
        <div><button type="submit" className="cs-btn cs-btn-primary" disabled={busy || !name.trim()}>{busy ? 'Creating…' : 'Create app'}</button></div>
      </form>
    </ModuleFrame.Panel>
  );
}
